import { Box, Typography, useTheme } from "@mui/material";
import {
  RcraInfoFederalWasteGeneratorStatus,
  StateRegulatoryStatus,
} from "generated-graphql/graphql";
import { useMemo } from "react";

import { FederalGeneratorStatusIndicator } from "../../FederalGeneratorStatusIndicator";
import { GeneratorStatusKind } from "../../getFederalGeneratorStatusColorSettings";
import { StateGeneratorStatusIndicator } from "../../StateGeneratorStatusIndicator";

type GeneratorStatusGridProps = {
  calculatedFederalGeneratorStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  reportedFederalGeneratorStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  calculatedStateGeneratorStatus?: StateRegulatoryStatus | null;
  reportedStateGeneratorStatus?: StateRegulatoryStatus | null;
  state?: string | null;
  showStateRegulatedWaste: boolean;
};

export const GeneratorStatusGrid: React.FC<GeneratorStatusGridProps> = ({
  calculatedFederalGeneratorStatus,
  reportedFederalGeneratorStatus,
  calculatedStateGeneratorStatus,
  reportedStateGeneratorStatus,
  state,
  showStateRegulatedWaste,
}) => {
  const theme = useTheme();
  const stateCode = state ?? "";

  const paddingSx = { py: 0.5, px: 1 };
  const borderTop = { borderTop: 1, borderColor: theme.palette.grey[200] };

  const hasCalculatedColumn = useMemo(() => {
    return calculatedFederalGeneratorStatus || calculatedStateGeneratorStatus;
  }, [calculatedFederalGeneratorStatus, calculatedStateGeneratorStatus]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: hasCalculatedColumn
          ? `140px minmax(100px, 1fr) minmax(130px, 1fr)`
          : `140px minmax(100px, 1fr)`,
      }}
    >
      <Box />
      <Box
        sx={{
          textAlign: hasCalculatedColumn ? "left" : "center",
          ...paddingSx,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Reported
        </Typography>
      </Box>
      {hasCalculatedColumn && (
        <Box sx={{ ...paddingSx }}>
          <Typography variant="caption" color="text.secondary">
            Calculated
          </Typography>
        </Box>
      )}

      <Box sx={{ ...paddingSx, ...borderTop }}>
        <Typography variant="caption" color="text.secondary">
          Federal status:
        </Typography>
      </Box>

      <Box
        sx={{
          ...paddingSx,
          ...borderTop,
          textAlign: hasCalculatedColumn ? "left" : "center",
        }}
      >
        <FederalGeneratorStatusIndicator
          status={reportedFederalGeneratorStatus ?? null}
          type="chip"
          active
          kind={GeneratorStatusKind.REPORTED}
        />
      </Box>

      {hasCalculatedColumn && (
        <Box sx={{ ...paddingSx, ...borderTop }}>
          <FederalGeneratorStatusIndicator
            status={calculatedFederalGeneratorStatus ?? null}
            type="chip"
            active
            kind={GeneratorStatusKind.CALCULATED}
          />
        </Box>
      )}

      {showStateRegulatedWaste && (
        <>
          <Box sx={{ ...paddingSx, ...borderTop }}>
            <Typography variant="caption" color="text.secondary">
              {state ? `${state} state status:` : "State status:"}
            </Typography>
          </Box>

          <Box
            sx={{
              ...paddingSx,
              ...borderTop,
              textAlign: hasCalculatedColumn ? "left" : "center",
            }}
          >
            <StateGeneratorStatusIndicator
              state={stateCode}
              status={reportedStateGeneratorStatus}
            />
          </Box>

          {hasCalculatedColumn && (
            <Box sx={{ ...paddingSx, ...borderTop }}>
              <StateGeneratorStatusIndicator
                state={stateCode}
                status={calculatedStateGeneratorStatus}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
